import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';

import useNetworkStatus from '@shared/hook/useGetNetworkStatus';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import NextNProgress from 'nextjs-progressbar';
import { useEffect, useRef } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { autoLogin } from '../reducers/auth';
import { rtlCache } from '../rtl-cache';
import { AppDispatch, persistor, store } from '../stores';
import '../styles/globals.css';
import getToken from '../utils/auth/getToken';

const AutoLogin = () => {
  const dispatch: AppDispatch = useDispatch();
  const { networkStatus } = useNetworkStatus();
  useEffect(() => {
    const token = getToken();
    if (token) {
      if (networkStatus) dispatch(autoLogin(token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return null;
};

// const FullPageLoader = () => (
//   <Stack h="100vh" w="100vh" justify="center" align="center">
//     <LoadingOverlay visible />
//   </Stack>
// );

const GA_MEASUREMENT_ID = 'G-288N6VNF93';

const MyApp = ({ Component, pageProps }: AppProps) => {
  const queryClientRef = useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 0,
          networkMode: 'always',
          staleTime: 15 * 1000,
        },
        mutations: {
          retry: 0,
          networkMode: 'always',
        },
      },
    })
  );

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>

      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} strategy="afterInteractive" />

      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
        
          gtag('config', '${GA_MEASUREMENT_ID}}');        
        `}
      </Script>
      <Script src="https://sentry.hamravesh.com/js-sdk-loader/81423a92103066dcb4a2ed0a6cb2faa7.min.js" />
      {process.env.NODE_ENV !== 'development' && (
        <Script id="google-tag-manager" strategy="afterInteractive">
          {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-WSQ3TGDP');
      `}
        </Script>
      )}

      {/* <Script id="in-track">
        {`
          var inTrack_config = {
            app_key: 'AAAAlw',
            auth_key: '84d71af3-ef0e-4ee4-8401-5c3a1b145314',
            public_key: 'BIEKHbzLrH8wuQLACCff59xBg5Fuk3p7b6MGabXXgMvAXqafW7RL9xCc56fcRvllPQvPu4DobhAwGnJEnJbsB-M='
          };
          (function (i, n, t, r, a, c, k) {
            o = i['InTrack'] = i['InTrack'] || {}; i[a] = i[a] || function () { (o.q = o.q || []).push(arguments); };
            s = n.createElement(t); s.async = true; s.src = r; s.onload = function () { o.init(c) };
            e = n.getElementsByTagName(t)[0]; e.parentNode.insertBefore(s, k);
          })(window, document, 'script', "//static1.intrack.ir/api/web/download/sdk/v1/inTrack.min.js?v=00",
          "Intk", inTrack_config);
        `}
      </Script> */}

      <Provider store={store}>
        <MantineProvider
          withGlobalStyles
          withNormalizeCSS
          emotionCache={rtlCache}
          theme={{
            dir: 'rtl',
            fontFamily: 'dana',
            colorScheme: 'light',
            defaultRadius: 'md',
            colors: {
              cyan: [
                '#f0fafa',
                '#e2f2f2',
                '#bee5e3',
                '#99d8d5',
                '#7bccc9',
                '#69c4c1',
                '#5cc2bd',
                '#4daaa6',
                '#00afaa',
                '#2a8480',
              ],
              brand: [
                '#e7e5e4',
                '#d6d3d1',
                '#a8a29e',
                '#78716c',
                '#57534e',
                '#44403c',
                '#292524',
                '#1c1917',
                '#0c0a09',
                '#0c0a09',
              ],
            },

            white: '#FEFEFE',
            primaryColor: 'brand',
          }}
        >
          <PersistGate persistor={persistor}>
            <QueryClientProvider client={queryClientRef.current}>
              <div dir="rtl">
                <AutoLogin />
                <Notifications position="top-center" />
                <NextNProgress color="#00afaa" />
                <Component {...pageProps} />
              </div>
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
          </PersistGate>
        </MantineProvider>
      </Provider>
    </>
  );
};

export default MyApp;
